import { Coordinates, CyvlTask, Job, Label, LabelInstance, mapFirst } from '@shared';
import * as turf from '@turf/turf';
import { ObjectState } from 'cvat-core-wrapper';
import { z } from 'zod';
import { DeduplicationPair, DeduplicationPairLabel } from './model';
import { DEFAULT_MAP_LOCATION } from '@shared/map';

export class DeduplicationTask extends CyvlTask {
    static parse = (jobRaw: unknown): DeduplicationTask => {
        const task = DeduplicationTask.safeParse(jobRaw);
        if (!task.success) throw task.error;
        return task.data;
    };

    static safeParse = (jobRaw: unknown): z.SafeParseReturnType<any, DeduplicationTask> => {
        const job = Job.safeParse(jobRaw);
        if (!job.success) return job;

        const { labels } = job.data;

        // Find deduplication_pair label
        const pairLabel = mapFirst(labels, DeduplicationPairLabel.safeParse);
        if (!pairLabel.success) {
            return pairLabel;
        }

        return { success: true, data: new DeduplicationTask(labels, pairLabel.data) };
    };

    constructor(
        readonly labels: Label[],
        readonly pairLabel: DeduplicationPairLabel,
    ) {
        super(labels);
    }

    defaultViewState!: ReturnType<typeof this.computeDefaultViewState>;
    defaultLocation!: ReturnType<typeof this.computeDefaultLocation>;
    bounds!: [number, number, number, number];
    pairData?: DeduplicationPair;
    frame!: number;

    onUpdate() {
        const pair = this.getFrameObjects(this.pairLabel)[0];
        if (!pair) return;
        this.pairData = pair.values.pair_data;
        this.frame = pair.state.frame;
        this.defaultViewState = this.computeDefaultViewState();
        this.defaultLocation = this.computeDefaultLocation();
        this.bounds = this.computeBounds();
    }

    private computeDefaultViewState() {
        if (!this.pairData) return DEFAULT_MAP_LOCATION;
        return this.pairData.camera_left?.location && this.pairData.camera_right?.location
            ? {
                  longitude: this.pairData.camera_left.location.lon,
                  latitude: this.pairData.camera_left.location.lat,
                  zoom: 18,
              }
            : DEFAULT_MAP_LOCATION;
    }

    private computeBounds() {
        const features: turf.Feature[] = [];
        console.log('computing bounds for pair data', this.pairData);

        if (this.pairData?.camera_left?.location) {
            features.push(turf.point([this.pairData.camera_left.location.lon, this.pairData.camera_left.location.lat]));
        }
        if (this.pairData?.camera_right?.location) {
            features.push(turf.point([this.pairData.camera_right.location.lon, this.pairData.camera_right.location.lat]));
        }

        const bounds = turf.bbox(
            turf.featureCollection(
                features.length > 0 ? features : [turf.point([DEFAULT_MAP_LOCATION.longitude, DEFAULT_MAP_LOCATION.latitude])],
            ),
        );
        return bounds as [number, number, number, number];
    };

    private computeDefaultLocation(): Coordinates {
        const viewState = this.defaultViewState;
        return { lat: viewState.latitude, lon: viewState.longitude };
    }

    /**
     * Get the deduplication pair tag for a given frame.
     */
    getCurrentPair(frame: number): LabelInstance<DeduplicationPairLabel, ObjectState> | undefined {
        return this.getFrameObjects(this.pairLabel).find((p) => p.state.frame === frame);
    }
}

